import { useQuery } from '@tanstack/react-query'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { getEnvironment } from '../../lib/environment'
import { Button } from '../../ui/Button/Button'
import { StatePanel } from '../../ui/StatePanel/StatePanel'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import { TenantProvider } from '../tenant/TenantProvider'
import type { TenantRepository } from '../tenant/tenant-repository'
import { authorizationTenantRepository } from '../tenant/authorization-tenant-repository'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import { onboardingStatusQueryKey } from './onboarding-query'
import { destinationPath, routeOnboardingResponse } from './onboarding-routing'
import styles from './OnboardingLayout.module.css'

type ReadyClinicGateProps = {
  enabled?: boolean
  client?: OnboardingApiClient
  tenantRepository?: TenantRepository
}

export function ReadyClinicGate({
  enabled = getEnvironment().onboardingGateEnabled,
  client = getOnboardingApiClient(),
  tenantRepository = authorizationTenantRepository,
}: ReadyClinicGateProps) {
  const auth = useAuth()
  const location = useLocation()
  const authenticatedUser = auth.status === 'authenticated' ? auth.user : null
  const organization = useQuery({
    queryKey: ['organization-admin', authenticatedUser?.uid ?? null],
    queryFn: () => client.getOrganization!(authenticatedUser!),
    enabled: enabled && authenticatedUser !== null && !!client.getOrganization,
    retry: false,
    staleTime: 30_000,
  })
  const query = useQuery({
    queryKey: authenticatedUser
      ? onboardingStatusQueryKey(authenticatedUser.uid)
      : ['onboarding', 'status', 'no-user'],
    queryFn: () => {
      if (!authenticatedUser) throw new OnboardingApiError('unauthorized')
      return client.getStatus(authenticatedUser)
    },
    enabled: enabled && authenticatedUser !== null,
    staleTime: 0,
  })

  if (!enabled) {
    return (
      <TenantProvider repository={tenantRepository}>
        <Outlet />
      </TenantProvider>
    )
  }
  if (auth.status === 'hydrating') {
    return <Spinner fullPage label="Restoring your session" />
  }
  if (auth.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  if (client.getOrganization && organization.isPending) {
    return <Spinner fullPage label="Checking organization access" />
  }
  if (
    organization.data?.canAddClinic &&
    ['OWNER', 'ADMIN'].includes(organization.data.role) &&
    organization.data.clinics.length === 0
  ) {
    return (
      <TenantProvider repository={tenantRepository} includeCallerClinic={false}>
        <Outlet />
      </TenantProvider>
    )
  }
  if (query.isPending) {
    return <Spinner fullPage label="Checking clinic readiness" />
  }
  if (query.isError) {
    if (
      query.error instanceof OnboardingApiError &&
      query.error.code === 'unauthorized'
    ) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname, reauthenticate: true }}
        />
      )
    }
    const classificationRequired =
      query.error instanceof OnboardingApiError &&
      query.error.code === 'classification-required'
    return (
      <main className={styles.page}>
        <div className={styles.shell}>
          <StatePanel
            kind="error"
            title={
              classificationRequired
                ? 'Account classification required'
                : 'Clinic readiness unavailable'
            }
            description={
              classificationRequired
                ? 'Flio support must review this account before it can continue.'
                : 'Retry this check. If it continues to fail, contact Flio support.'
            }
          />
          <div className={styles.actions}>
            <Button type="button" onClick={() => void query.refetch()}>
              Retry
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void auth.logout()}
            >
              Sign out
            </Button>
          </div>
        </div>
      </main>
    )
  }

  const destination = routeOnboardingResponse(query.data)
  if (destination !== 'dashboard' && destination !== 'legacy-dashboard') {
    return <Navigate to={destinationPath(destination)} replace />
  }

  const includeCallerClinic = !(
    query.data.schemaVersion === 2 && query.data.principalType === 'NON_OWNER'
  )
  return (
    <TenantProvider
      repository={tenantRepository}
      includeCallerClinic={includeCallerClinic}
    >
      <Outlet />
    </TenantProvider>
  )
}
